import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../Styles/Mp3Form.css";
import { fileAPI, mp3API } from "../../api/index"; // adjust to your actual api file path

// Reads an audio file's duration client-side before uploading, using
// a throwaway <audio> element and an object URL. Resolves in whole seconds.
const getAudioDuration = (file) =>
  new Promise((resolve, reject) => {
    const audio = new Audio();
    const objectUrl = URL.createObjectURL(file);
    audio.src = objectUrl;
    audio.onloadedmetadata = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(Math.round(audio.duration));
    };
    audio.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Could not read audio metadata from this file."));
    };
  });

function Mp3Form() {
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("active");
  const [file, setFile] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) setFile(selected);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!file) {
      setError("Please choose an MP3 file to upload.");
      return;
    }

    setSubmitting(true);
    try {
      // Step 1: read duration from the raw file before it ever leaves the browser
      let durationSeconds = null;
      try {
        durationSeconds = await getAudioDuration(file);
      } catch (durationErr) {
        console.warn("Duration read failed, continuing without it:", durationErr);
      }

      // Step 2: upload to the "mp3" subfolder per your Swagger dropdown
      const uploadRes = await fileAPI.upload(file, "mp3");
      const fileUrl =
        uploadRes.data?.url ||
        uploadRes.data?.file_url ||
        uploadRes.data?.photo_url;

      if (!fileUrl) {
        throw new Error(
          "Upload succeeded but the response didn't contain a usable URL field."
        );
      }

      // Step 3: create the mp3 record
      await mp3API.create({
        title,
        description: description || null,
        status,
        file_url: fileUrl,
        file_name: file.name,
        duration_seconds: durationSeconds,
      });

      navigate("/dashboard/mp3-table");
    } catch (err) {
      console.error("MP3 save failed:", err);
      setError(
        err.response?.data?.detail || err.message || "Something went wrong while saving the song."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Add MP3 Song</h2>
        <button className="back-btn-mp3" onClick={() => navigate(-1)}>
          Back
        </button>
      </div>

      <form className="mp3-form" onSubmit={handleSubmit}>
        {error && <div className="mp3-form-error">{error}</div>}

        <div className="form-group">
          <label>Song Title</label>
          <input
            type="text"
            placeholder="Enter Song Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label>Upload MP3</label>
          <input type="file" accept=".mp3,audio/*" onChange={handleFileChange} required />
        </div>

        <div className="form-group">
          <label>Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            rows="4"
            placeholder="Optional Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          ></textarea>
        </div>

        <button className="sub-btn" type="submit" disabled={submitting}>
          {submitting ? "Uploading..." : "Submit MP3 Song"}
        </button>
      </form>
    </div>
  );
}

export default Mp3Form;